import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { entitiesApi, projectsApi, tasksApi, calendarApi, expendituresApi, notesApi, contactsApi, recurringApi, digestApi } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, Pencil, Trash2, Paperclip, Send, ExternalLink, Wand2, Sparkles } from "lucide-react";
import EntityDialog from "@/components/EntityDialog";
import ProjectDialog from "@/components/ProjectDialog";
import TaskDialog from "@/components/TaskDialog";
import UniversalQuickAdd from "@/components/UniversalQuickAdd";
import EventDialog from "@/components/EventDialog";
import ExpenditureDialog from "@/components/ExpenditureDialog";
import EditScopeDialog from "@/components/EditScopeDialog";
import RecurringUpcomingPanel from "@/components/RecurringUpcomingPanel";
import NewFromTemplateDialog from "@/components/NewFromTemplateDialog";
import ProjectRosterPanel from "@/components/ProjectRosterPanel";
import { useAuth } from "@/lib/auth";
import ProjectHealth, { healthSummary } from "@/components/ProjectHealth";
import { templatesApi } from "@/lib/api";
import NoteDialog from "@/components/NoteDialog";
import ContactDialog from "@/components/ContactDialog";
import EmailDialog from "@/components/EmailDialog";
import BudgetWarning from "@/components/BudgetWarning";
import OverviewDerived from "@/components/OverviewDerived";
import EntityOrganizationsPanel from "@/components/EntityOrganizationsPanel";
import ProjectRelatedContext from "@/components/ProjectRelatedContext";
import CompassSection from "@/components/compass/CompassSection";
import RoutingContextEditor from "@/components/RoutingContextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/currency";
import { useFocusHighlight } from "@/lib/useFocusHighlight";
import { toast } from "sonner";

// Convert a Quick Add draft into the shape each detailed dialog expects (no id → creates new)
function draftToPreset(rec) {
  const f = rec.fields || {};
  const common = { entity_id: f.entity_id || "", project_id: f.project_id || "" };
  switch (rec.type) {
    case "project": return { ...common,
      name: f.name || "", description: f.description || "", status: f.status || "Planning",
      priority: f.priority || "Medium", phase: f.phase || "",
      start_date: f.start_date || "", target_completion: f.target_completion || "",
      budget: f.budget || 0, next_action: f.next_action || "",
      owner_contact_id: f.owner_contact_id || "",
    };
    case "task": return { ...common,
      name: f.title || "", description: f.description || "", status: f.status || "Not started",
      priority: f.priority || "Normal", due_date: f.due_date || "",
      assignee_contact_id: f.assignee_contact_id || "", tags: f.tags || [],
    };
    case "calendar": return { ...common,
      contact_id: f.contact_id || "", title: f.title || "", description: f.description || "",
      event_type: f.event_type || "meeting", starts_at: f.starts_at || "", ends_at: f.ends_at || "",
      location: f.location || "",
      participant_contact_ids: f.participant_contact_ids || [],
    };
    case "expenditure": return { ...common,
      amount: f.amount || 0, date: f.date || new Date().toISOString().slice(0,10),
      description: f.description || "", category: f.category || "General",
      vendor: f.vendor || "", kind: f.kind || "expense",
    };
    case "note": return { ...common,
      title: f.title || "", content: f.content || "", entry_type: f.entry_type || "note",
      date: f.date || new Date().toISOString().slice(0,10),
      decision_reason: f.decision_reason || "", responsible_contact_id: f.responsible_contact_id || "",
    };
    case "contact": return {
      full_name: f.full_name || "", email: f.email || "", phone: f.phone || "",
      company: f.company || "", role: f.role || "",
      preferred_channel: f.preferred_channel || "email", notes: f.notes || "",
    };
    default: return null;
  }
}

function Badge({ children, color = "white" }) {
  return (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border" style={{ borderColor: `${color}44`, color, background: `${color}12` }}>
      {children}
    </span>
  );
}

const PRIORITY_COLORS = { Critical: "#EF4444", High: "#F59E0B", Medium: "#3B82F6", Normal: "#3B82F6", Low: "#94A3B8" };
const STATUS_COLORS = { Open: "#94A3B8", "Not started": "#94A3B8", "In Progress": "#3B82F6", Blocked: "#EF4444", Done: "#10B981",
  Planning: "#94A3B8", Active: "#3B82F6", "On Hold": "#F59E0B", Completed: "#10B981" };

export default function EntityWorkspace() {
  const { user } = useAuth();
  const isAdmin = (user?.platform_role || user?.role) === "admin";
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";

  const [entity, setEntity] = useState(null);
  // Explicit load states so the page can never wedge on "Loading…". A
  // restricted Member's supporting endpoints (budget summary, recurring
  // preview) legitimately return 403 — those must never block the core
  // entity render.
  const [loadState, setLoadState] = useState("loading"); // "loading" | "loaded" | "denied" | "failed"
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [exps, setExps] = useState([]);
  const [upcomingRec, setUpcomingRec] = useState([]);
  const [budget, setBudget] = useState(null);
  const [overview, setOverview] = useState(null);
  const [notes, setNotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emails, setEmails] = useState([]);

  // Focus deep-link highlight — read `?focus={id}`, scroll to
  // `[data-record-id="{id}"]`, briefly pulse, then strip the param.
  // readyKey composes every tab's committed data so the helper only
  // scans the DOM once the relevant records have rendered.
  useFocusHighlight(`${tab}:${tasks.length}:${projects.length}:${notes.length}:${events.length}:${exps.length}`);

  const [editEntity, setEditEntity] = useState(false);
  const [projDlg, setProjDlg] = useState({ open: false, project: null });
  const [projRcDlg, setProjRcDlg] = useState({ open: false, project: null });
  const [taskDlg, setTaskDlg] = useState({ open: false, task: null });
  const [evDlg, setEvDlg] = useState({ open: false, event: null });
  const [expDlg, setExpDlg] = useState({ open: false, exp: null, initialKind: "expense" });
  const [scopeDlg, setScopeDlg] = useState({ open: false, exp: null });
  const [tplLaunch, setTplLaunch] = useState({ open: false, template: null });
  const [tplPicker, setTplPicker] = useState(false);
  const [tplList, setTplList] = useState([]);
  const [noteDlg, setNoteDlg] = useState({ open: false, note: null });
  const [contactDlg, setContactDlg] = useState({ open: false, contact: null });
  const [emailDlg, setEmailDlg] = useState({ open: false, contact: null });
  const [quick, setQuick] = useState({ open: false, section: "tasks" });

  const openQuick = (section) => setQuick({ open: true, section });
  const handleQuickEditDetails = (rec) => {
    const preset = draftToPreset(rec);
    if (!preset) return;
    if (rec.type === "project") setProjDlg({ open: true, project: preset });
    else if (rec.type === "task") setTaskDlg({ open: true, task: preset });
    else if (rec.type === "calendar") setEvDlg({ open: true, event: preset });
    else if (rec.type === "expenditure") setExpDlg({ open: true, exp: preset });
    else if (rec.type === "note") setNoteDlg({ open: true, note: preset });
    else if (rec.type === "contact") setContactDlg({ open: true, contact: preset });
    else toast.info("Advanced form not available for this type");
  };

  const load = async () => {
    // `entitiesApi.get(id)` is the ONE required call — if the caller
    // can't see the entity at all, we surface Permission denied / Failed
    // to load. Every other request is a supporting fetch: some (budget
    // summary, recurring preview) are admin-only by policy and legitimately
    // 403 for a restricted Member assigned to a project. Under the old
    // `Promise.all`, that single 403 short-circuited the whole load and
    // the page rendered "Loading…" forever. `Promise.allSettled` lets
    // each call succeed/fail independently.
    const results = await Promise.allSettled([
      entitiesApi.get(id),                             // 0 — REQUIRED
      projectsApi.list(id),                            // 1
      tasksApi.list({ entity_id: id }),                // 2
      calendarApi.list({ entity_id: id }),             // 3
      expendituresApi.list({ entity_id: id }),         // 4
      expendituresApi.budgetSummary(id),               // 5 — admin-only (403 ok)
      notesApi.list({ entity_id: id }),                // 6
      contactsApi.list({ entity_id: id }),             // 7
      entitiesApi.overview(id),                        // 8
      recurringApi.upcoming(id, 60),                   // 9
    ]);

    const ent = results[0];
    if (ent.status !== "fulfilled") {
      const status = ent.reason?.response?.status;
      // 404 hides existence from a caller with no visibility — surface
      // as an explicit Permission denied so the URL bar is honest
      // without leaking whether the entity really exists.
      if (status === 404 || status === 403) setLoadState("denied");
      else setLoadState("failed");
      return;
    }
    setEntity(ent.value.data);

    // Supporting data — fall back to sensible empties on any failure so
    // the tab still renders. `.value?.data ?? fallback` is intentional:
    // an axios success without a payload should also default.
    const pick = (i, fallback) =>
      results[i].status === "fulfilled" ? (results[i].value?.data ?? fallback) : fallback;
    setProjects(pick(1, []));
    setTasks(pick(2, []));
    setEvents(pick(3, []));
    setExps(pick(4, []));
    setBudget(pick(5, null));
    setNotes(pick(6, []));
    setContacts(pick(7, []));
    setOverview(pick(8, null));
    setUpcomingRec(pick(9, []));
    setLoadState("loaded");
  };

  // Route the "edit" click on a transaction: rule-generated rows go through
  // a scope picker, one-off rows go straight to the transaction editor.
  const requestEditExpenditure = (exp) => {
    if (exp?.recurring_rule_id) {
      setScopeDlg({ open: true, exp });
    } else {
      setExpDlg({ open: true, exp, initialKind: (exp?.kind || "expense") });
    }
  };

  // Load the underlying recurring rule and open the editor for it.
  const openRuleEditor = async (ruleId) => {
    try {
      const list = await recurringApi.list(id);
      const rule = (list.data || []).find(r => r.id === ruleId);
      if (!rule) { toast.error("Recurring rule not found"); return; }
      setExpDlg({ open: true, exp: { ...rule, __isRecurringRule: true }, initialKind: rule.kind || "expense" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load rule");
    }
  };

  useEffect(() => { load(); }, [id]);

  // Live-refresh derived overview: on focus, visibility change, and every 60s while the tab is mounted.
  useEffect(() => {
    if (!id) return;
    const refreshOverview = async () => {
      try {
        const ov = await entitiesApi.overview(id);
        setOverview(ov.data);
      } catch { /* ignore transient errors */ }
    };
    const onFocus = () => refreshOverview();
    const onVisibility = () => { if (document.visibilityState === "visible") refreshOverview(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const iv = setInterval(refreshOverview, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(iv);
    };
  }, [id]);

  if (loadState === "denied") return (
    <div className="p-8 max-w-lg mx-auto text-center" data-testid="entity-denied">
      <div className="text-eyebrow mb-2">Permission denied</div>
      <h1 className="text-2xl font-display font-semibold mb-3">
        You don't have access to this workspace
      </h1>
      <p className="text-sm text-white/60 mb-6">
        Ask an admin to add you to a project inside this entity.
      </p>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
            data-testid="entity-denied-back">
        <ArrowLeft className="w-3.5 h-3.5"/> Back to my projects
      </Link>
    </div>
  );
  if (loadState === "failed") return (
    <div className="p-8 max-w-lg mx-auto text-center" data-testid="entity-failed">
      <div className="text-eyebrow mb-2">Failed to load</div>
      <h1 className="text-2xl font-display font-semibold mb-3">
        Something went wrong loading this workspace
      </h1>
      <p className="text-sm text-white/60 mb-6">Try again in a moment.</p>
      <Button onClick={() => { setLoadState("loading"); load(); }}
              className="rounded-full" data-testid="entity-retry-btn">
        Retry
      </Button>
    </div>
  );
  if (!entity) return <div className="p-8 text-white/50" data-testid="entity-loading">Loading…</div>;

  const setTab = (v) => setSp({ tab: v });
  const accent = entity.color || "#3B82F6";

  const decisions = notes.filter((n) => n.entry_type === "decision");
  const generalNotes = notes.filter((n) => n.entry_type !== "decision");

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden max-w-full" data-testid="entity-workspace">
      {/* Header */}
      <div className="relative border-b border-white/10">
        {entity.background_image && (
          <>
            <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url(${entity.background_image})` }} />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14]/40 via-[#0B0E14]/70 to-[#0B0E14]" />
          </>
        )}
        <div className="relative px-4 md:px-8 pt-6 pb-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors mb-4" data-testid="back-to-hq">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to HQ
          </Link>
          <div className="flex items-start justify-between gap-4 md:gap-6 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: accent }} />
                <div className="text-eyebrow truncate">{entity.phase || entity.status || "Active"}</div>
              </div>
              <h1 className="font-display text-2xl md:text-4xl font-semibold tracking-tight break-words">{entity.name}</h1>
              {entity.description && <p className="text-white/60 mt-2 max-w-2xl text-sm leading-relaxed break-words">{entity.description}</p>}
            </div>
            <Button variant="ghost" onClick={() => setEditEntity(true)} data-testid="edit-entity-btn" className="text-white/60 hover:text-white hover:bg-white/10 flex-shrink-0">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          </div>
        </div>

        <div className="relative px-4 md:px-8 pb-0 -mx-4 md:mx-0 overflow-x-auto no-scrollbar">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-transparent border-none p-0 h-auto gap-1 flex-nowrap w-max px-4 md:px-0" data-testid="workspace-tabs">
              {["overview","projects","tasks","calendar","budget","notes","organizations","contacts","email"]
                .filter(t => isAdmin || t !== "budget")
                .map((t) => (
                <TabsTrigger
                  key={t}
                  value={t}
                  data-testid={`tab-${t}`}
                  className="capitalize data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50 rounded-md px-3 py-2 text-sm whitespace-nowrap flex-shrink-0"
                >{t}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-full">
        {tab === "overview" && (
          <div className="space-y-6">
            <OverviewDerived
              overview={overview}
              entityId={id}
              onEditEntity={() => setEditEntity(true)}
              loading={!overview}
            />
            {isAdmin && (
              <RoutingContextEditor
                kind="entity" record={entity}
                api={entitiesApi} canEdit
                onSaved={async () => { const e = await entitiesApi.get(id); setEntity(e.data); }}
              />
            )}
          </div>
        )}

        {tab === "projects" && (
          <Section
            title="Projects"
            action={
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => openQuick("projects")} data-testid="add-project-btn" className="rounded-full bg-white text-black hover:bg-white/90"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
                <Button
                  onClick={async () => {
                    try {
                      const r = await templatesApi.list(false);
                      setTplList(r.data || []);
                      setTplPicker(true);
                    } catch { toast.error("Failed to load templates"); }
                  }}
                  variant="outline"
                  data-testid="new-from-template-btn"
                  className="rounded-full border-white/15 text-white hover:bg-white/10"
                ><Plus className="w-3.5 h-3.5 mr-1.5"/>New from template</Button>
                <Button onClick={() => setProjDlg({ open: true, project: null })} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 text-xs" data-testid="add-project-advanced-btn"><Plus className="w-3.5 h-3.5 mr-1.5"/>Advanced</Button>
              </div>
            }
          >
            <div className="grid md:grid-cols-2 gap-4">
              {projects.length === 0 && <Empty label="No projects yet." />}
              {projects.map(p => (
                <div key={p.id} className="p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid={`project-card-${p.id}`} data-record-id={p.id}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-semibold text-lg break-words">{p.name}</h3>
                      {p.health && <div className="mt-1"><ProjectHealth health={p.health} compact testid={`project-health-${p.id}`}/></div>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <button onClick={() => setProjRcDlg({ open: true, project: p })} className="p-1.5 rounded hover:bg-white/10 text-white/60" data-testid={`routing-context-project-btn-${p.id}`} title="Routing Context">
                        <Sparkles className="w-3.5 h-3.5"/>
                      </button>
                      <button onClick={() => setProjDlg({ open: true, project: p })} className="p-1.5 rounded hover:bg-white/10 text-white/60" data-testid={`edit-project-${p.id}`}><Pencil className="w-3.5 h-3.5"/></button>
                      <button onClick={async () => { if (window.confirm("Delete project?")) { await projectsApi.remove(p.id); toast.success("Project deleted"); load(); } }} className="p-1.5 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                  {p.health && p.health.reasons?.length > 0 && (
                    <div className="mb-3"><ProjectHealth health={p.health} compact={false} testid={`project-health-detail-${p.id}`}/></div>
                  )}
                  {p.description && <p className="text-sm text-white/60 mb-3">{p.description}</p>}
                  {p.template_name && <div className="text-[10px] text-white/40 mb-3">From template: <span className="text-white/60">{p.template_name}</span></div>}
                  {(p.mission || p.goals || p.scope || p.instructions || p.deliverables) && (
                    <div className="mb-3 p-3 rounded-lg bg-white/[0.03] border border-white/5 space-y-1.5" data-testid={`project-briefing-${p.id}`}>
                      {p.mission && <div className="text-xs"><span className="text-white/40 uppercase text-[10px] tracking-wider">Mission:</span> <span className="text-white/85">{p.mission}</span></div>}
                      {p.goals && <div className="text-xs"><span className="text-white/40 uppercase text-[10px] tracking-wider">Goals:</span> <span className="text-white/85">{p.goals}</span></div>}
                      {p.scope && <div className="text-xs"><span className="text-white/40 uppercase text-[10px] tracking-wider">Scope:</span> <span className="text-white/85">{p.scope}</span></div>}
                      {p.instructions && <div className="text-xs"><span className="text-white/40 uppercase text-[10px] tracking-wider">Instructions:</span> <span className="text-white/85">{p.instructions}</span></div>}
                      {p.deliverables && <div className="text-xs"><span className="text-white/40 uppercase text-[10px] tracking-wider">Deliverables:</span> <span className="text-white/85">{p.deliverables}</span></div>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <Badge color={STATUS_COLORS[p.status]}>{p.status}</Badge>
                    <Badge color={PRIORITY_COLORS[p.priority]}>{p.priority}</Badge>
                    {p.phase && <Badge color="#94A3B8">{p.phase}</Badge>}
                  </div>
                  {p.permissions?.view_financials && (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-xs text-white/60" data-testid={`project-financials-${p.id}`}>
                        <div><div className="text-white/40 uppercase text-[10px] tracking-wider">Budget</div><div className="text-white/90 mt-0.5">{fmtMoney(p.budget, p.currency || entity.currency || "USD")}</div></div>
                        <div><div className="text-white/40 uppercase text-[10px] tracking-wider">Spent</div><div className={`mt-0.5 ${Number(p.spent||0) > Number(p.budget||0) && Number(p.budget||0) > 0 ? "text-amber-300" : "text-white/90"}`}>{fmtMoney(p.spent, p.currency || entity.currency || "USD")}</div></div>
                        <div><div className="text-white/40 uppercase text-[10px] tracking-wider">Remaining</div><div className={`mt-0.5 ${(Number(p.budget||0) - Number(p.spent||0)) < 0 ? "text-amber-300" : "text-white/90"}`}>{fmtMoney((Number(p.budget||0) - Number(p.spent||0)), p.currency || entity.currency || "USD")}</div></div>
                      </div>
                      <BudgetWarning budget={p.budget} spent={p.spent} currency={p.currency || entity.currency || "USD"} className="mt-3"/>
                    </>
                  )}
                  {p.next_action && <div className="mt-3 pt-3 border-t border-white/10 text-sm"><span className="text-white/40 text-xs uppercase tracking-wider">Next:</span> <span className="text-white/85">{p.next_action}</span></div>}
                  {p.risks && <div className="mt-2 text-sm"><span className="text-red-400 text-xs uppercase tracking-wider">Risks:</span> <span className="text-white/85">{p.risks}</span></div>}
                  <ProjectRelatedContext projectId={p.id} />
                  <ProjectRosterPanel project={p} isAdmin={isAdmin}
                                       canManage={!!(p.permissions?.is_admin || p.permissions?.edit_project_settings)} />
                  <CompassSection projectId={p.id} />
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === "organizations" && (
          <EntityOrganizationsPanel entityId={id} />
        )}

        {tab === "tasks" && (
          <Section
            title="Tasks"
            action={
              <div className="flex flex-wrap gap-2 justify-end">
                <Button onClick={() => openQuick("tasks")} data-testid="add-task-btn" className="rounded-full bg-white text-black hover:bg-white/90"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
                <Button onClick={() => setTaskDlg({ open: true, task: null })} variant="ghost" data-testid="add-task-advanced-btn" className="text-white/60 hover:text-white hover:bg-white/10 text-xs"><Plus className="w-3.5 h-3.5 mr-1.5"/>Advanced</Button>
              </div>
            }
          >
            <TasksList tasks={tasks} contacts={contacts} projects={projects}
                       onEdit={(t) => setTaskDlg({ open: true, task: t })}
                       onDelete={async (t) => { if(window.confirm("Delete task?")){ await tasksApi.remove(t.id); toast.success("Deleted"); load();} }}
                       onToggle={async (t) => { await tasksApi.update(t.id, { status: t.status === "Done" ? "Not started" : "Done" }); load(); }}
                       canDelete={(t) => {
                         // §7 permission-aware task delete.  Backend is
                         // still authoritative; this just prevents the
                         // user from clicking a doomed action.
                         const p = (projects || []).find(pp => pp.id === t.project_id);
                         return !!(p?.permissions?.is_admin || p?.permissions?.edit_project_settings);
                       }} />
          </Section>
        )}

        {tab === "calendar" && (
          <Section
            title="Calendar & Milestones"
            action={
              <div className="flex gap-2">
                <Button onClick={() => openQuick("calendar")} data-testid="add-event-btn" className="rounded-full bg-white text-black hover:bg-white/90"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
                <Button onClick={() => setEvDlg({ open: true, event: null })} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 text-xs" data-testid="add-event-advanced-btn"><Plus className="w-3.5 h-3.5 mr-1.5"/>Advanced</Button>
              </div>
            }
          >
            <div className="space-y-2">
              {events.length === 0 && <Empty label="No events scheduled." />}
              {events.map(e => (
                <div key={e.id} className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-[#151A23]" data-record-id={e.id}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge color="#3B82F6">{e.event_type}</Badge>
                      <span className="text-xs text-white/50">{new Date(e.starts_at).toLocaleString()}</span>
                    </div>
                    <div className="font-medium">{e.title}</div>
                    {e.description && <div className="text-xs text-white/50 mt-1">{e.description}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEvDlg({ open: true, event: e })} className="p-1.5 rounded hover:bg-white/10 text-white/60"><Pencil className="w-3.5 h-3.5"/></button>
                    <button onClick={async () => { if(window.confirm("Delete event?")){ await calendarApi.remove(e.id); toast.success("Deleted"); load();} }} className="p-1.5 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === "budget" && isAdmin && (
          <Section
            title="Budget & Transactions"
            action={
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => openQuick("budget")} data-testid="add-expenditure-btn" className="rounded-full bg-white text-black hover:bg-white/90"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
                <Button onClick={() => setExpDlg({ open: true, exp: null, initialKind: "expense" })} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 text-xs" data-testid="add-expense-btn"><Plus className="w-3.5 h-3.5 mr-1.5"/>Add expense</Button>
                <Button onClick={() => setExpDlg({ open: true, exp: null, initialKind: "revenue" })} variant="ghost" className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 text-xs" data-testid="add-revenue-btn"><Plus className="w-3.5 h-3.5 mr-1.5"/>Add revenue</Button>
              </div>
            }
          >
            {(budget?.by_currency || []).length > 1 ? (
              <>
                {/* Multi-currency: Totals by Currency is the PRIMARY summary. */}
                <div className="mb-6 p-5 rounded-xl border border-white/10 bg-[#151A23]" data-testid="multi-currency-panel">
                  <div className="flex items-baseline justify-between mb-4">
                    <div className="text-eyebrow">Totals by currency</div>
                    <div className="text-[11px] text-white/40">Currencies are never combined into a single total.</div>
                  </div>
                  <div className="space-y-3">
                    {(budget?.by_currency || []).map((b) => (
                      <div key={b.currency} className="p-4 rounded-lg bg-white/[0.03] border border-white/5" data-testid={`ccy-row-${b.currency}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="text-base font-display font-semibold text-white">{b.currency}</div>
                          <BudgetWarning budget={b.approved_budget} spent={b.spent} currency={b.currency}/>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
                          <CcyMetric label="Approved" value={fmtMoney(b.approved_budget, b.currency)}/>
                          <CcyMetric label="Project Budgeted" value={fmtMoney(b.project_budget, b.currency)}/>
                          <CcyMetric label="Spent" value={fmtMoney(b.spent, b.currency)} accent={Number(b.spent) > Number(b.approved_budget) && Number(b.approved_budget) > 0 ? "amber" : undefined}/>
                          <CcyMetric label="Remaining" value={fmtMoney(b.remaining, b.currency)} accent={Number(b.remaining) < 0 ? "amber" : undefined}/>
                          <CcyMetric label="Revenue" value={fmtMoney(b.revenue, b.currency)}/>
                          <CcyMetric label="Outstanding" value={fmtMoney(b.outstanding, b.currency)}/>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-6 text-xs text-white/50" data-testid="entity-approved-note">
                  Entity approved allocation is <span className="text-white/80 font-medium">{fmtMoney(budget?.approved_budget, budget?.currency || "USD")}</span> in {budget?.currency || "USD"}. Project budgets sit in their own currencies above.
                </div>
              </>
            ) : (
              <div className="grid md:grid-cols-5 gap-4 mb-6">
                <Panel title="Entity Approved Budget" value={fmtMoney(budget?.approved_budget, budget?.currency || "USD")} />
                <Panel title="Entity Spent" value={fmtMoney(budget?.spent, budget?.currency || "USD")} accent={Number(budget?.spent||0) > Number(budget?.approved_budget||0) && Number(budget?.approved_budget||0) > 0 ? "amber" : undefined} />
                <Panel title="Entity Remaining" value={fmtMoney(budget?.remaining, budget?.currency || "USD")} accent={Number(budget?.remaining||0) < 0 ? "amber" : undefined} />
                <Panel title="Revenue" value={fmtMoney(budget?.revenue, budget?.currency || "USD")} />
                <Panel title="Outstanding" value={fmtMoney(budget?.outstanding, budget?.currency || "USD")} />
              </div>
            )}
            <div className="mb-4"><BudgetWarning budget={budget?.approved_budget} spent={budget?.spent} currency={budget?.currency || "USD"}/></div>
            <RecurringUpcomingPanel items={upcomingRec} projects={projects} onOpenRule={openRuleEditor} />
            <div className="space-y-2">
              {exps.length === 0 && (
                <div className="text-center py-10 border border-dashed border-white/10 rounded-xl" data-testid="budget-empty-state">
                  <div className="text-sm text-white/50 mb-3">No transactions yet — record an expense or revenue to bring this to life.</div>
                  <div className="flex justify-center gap-2 flex-wrap">
                    <Button onClick={() => setExpDlg({ open: true, exp: null, initialKind: "expense" })} data-testid="budget-empty-add-expense-btn" className="rounded-full bg-white text-black hover:bg-white/90">
                      <Plus className="w-4 h-4 mr-2"/> Add expense
                    </Button>
                    <Button onClick={() => setExpDlg({ open: true, exp: null, initialKind: "revenue" })} data-testid="budget-empty-add-revenue-btn" variant="outline" className="rounded-full border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10">
                      <Plus className="w-4 h-4 mr-2"/> Add revenue
                    </Button>
                  </div>
                </div>
              )}
              {exps.map(e => {
                const kind = (e.kind || "expense").toLowerCase();
                const status = (e.status || "").toLowerCase();
                const isRev = kind === "revenue";
                const isOutstanding = (isRev && status === "outstanding") || kind === "outstanding";
                const kindLabel = isRev ? "REVENUE" : kind === "expected" ? "EXPECTED" : "EXPENSE";
                const kindColor = isRev ? "#10B981" : kind === "expected" ? "#94A3B8" : "#EF4444";
                const proj = projects.find(pp => pp.id === e.project_id);
                const projLabel = proj?.name || "Entity-level";
                const counterparty = isRev ? (e.source_client || "") : (e.vendor || "");
                const counterpartyLabel = isRev ? "Client" : "Vendor";
                return (
                  <div key={e.id} className="flex items-start justify-between gap-3 p-4 rounded-lg border border-white/10 bg-[#151A23] flex-wrap sm:flex-nowrap" data-testid={`exp-row-${e.id}`} data-record-id={e.project_id || e.id}>
                    <div className="flex-1 min-w-0 w-full sm:w-auto">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge color={kindColor}>{kindLabel}</Badge>
                        {isRev && (
                          <span
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border"
                            data-testid={`exp-status-badge-${e.id}`}
                            style={isOutstanding
                              ? { color: "#F59E0B", borderColor: "#F59E0B55", backgroundColor: "#F59E0B12" }
                              : { color: "#10B981", borderColor: "#10B98155", backgroundColor: "#10B98112" }}>
                            {isOutstanding ? "Outstanding" : "Received"}
                          </span>
                        )}
                        <span className="text-xs text-white/50">{e.date} · {e.category} · {projLabel}</span>
                        {e.recurring_rule_id && (
                          <span
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-white/15 text-white/60 flex items-center gap-1"
                            data-testid={`exp-recurring-badge-${e.id}`}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 2l4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>
                            Recurring
                          </span>
                        )}
                        {e.attachment_file_id && <Paperclip className="w-3 h-3 text-white/40" />}
                        {e.attachment_url && <a href={e.attachment_url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-white"><ExternalLink className="w-3 h-3" /></a>}
                      </div>
                      <div className="font-medium break-words">{e.description || "(no description)"}</div>
                      {counterparty && <div className="text-xs text-white/50 mt-0.5">{counterpartyLabel}: {counterparty}</div>}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-lg font-semibold tabular-nums" style={{ color: isRev ? "#10B981" : "#F5F5F5" }} data-testid={`exp-amount-${e.id}`}>
                        {isRev ? "+" : ""}{fmtMoney(e.amount, e.currency || entity.currency || "USD")}
                      </div>
                      <button onClick={() => requestEditExpenditure(e)} data-testid={`exp-edit-${e.id}`} className="p-1.5 rounded hover:bg-white/10 text-white/60"><Pencil className="w-3.5 h-3.5"/></button>
                      <button onClick={async () => { if(window.confirm(`Delete this ${isRev ? "revenue" : "expense"} entry?`)){ await expendituresApi.remove(e.id); toast.success("Deleted"); load();} }} data-testid={`exp-delete-${e.id}`} className="p-1.5 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {tab === "notes" && (
          <Section
            title="Notes & Decision Log"
            action={
              <div className="flex gap-2">
                <Button onClick={() => openQuick("notes")} data-testid="add-note-btn" className="rounded-full bg-white text-black hover:bg-white/90"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
                <Button onClick={() => setNoteDlg({ open: true, note: null })} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 text-xs" data-testid="add-note-advanced-btn"><Plus className="w-3.5 h-3.5 mr-1.5"/>Advanced</Button>
              </div>
            }
          >
            <div className="space-y-3">
              {notes.length === 0 && <Empty label="No notes yet." />}
              {notes.map(n => (
                <div key={n.id} className="p-4 rounded-lg border border-white/10 bg-[#151A23]" data-record-id={n.id}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge color={n.entry_type === "decision" ? "#F59E0B" : "#94A3B8"}>{n.entry_type}</Badge>
                      <span className="text-xs text-white/50">{n.date || new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setNoteDlg({ open: true, note: n })} className="p-1.5 rounded hover:bg-white/10 text-white/60"><Pencil className="w-3.5 h-3.5"/></button>
                      <button onClick={async () => { if(window.confirm("Delete note?")){ await notesApi.remove(n.id); toast.success("Deleted"); load();} }} className="p-1.5 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                  <div className="font-display font-semibold mb-1">{n.title}</div>
                  <div className="text-sm text-white/70 whitespace-pre-wrap">{n.content}</div>
                  {n.entry_type === "decision" && n.decision_reason && (
                    <div className="mt-2 pt-2 border-t border-white/10 text-sm"><span className="text-white/40 text-xs uppercase tracking-wider">Reason:</span> <span className="text-white/85">{n.decision_reason}</span></div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === "contacts" && (
          <Section
            title="Related Contacts"
            action={
              <div className="flex gap-2">
                <Button onClick={() => openQuick("contacts")} data-testid="add-contact-btn" className="rounded-full bg-white text-black hover:bg-white/90"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>
                <Button onClick={() => setContactDlg({ open: true, contact: null })} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 text-xs" data-testid="add-contact-advanced-btn"><Plus className="w-3.5 h-3.5 mr-1.5"/>Advanced</Button>
              </div>
            }
          >
            <div className="grid md:grid-cols-2 gap-3">
              {contacts.length === 0 && <Empty label="No contacts linked yet. Add tasks with assignees or link contacts to associate them here." />}
              {contacts.map(c => (
                <Link key={c.id} to={`/contacts/${c.id}`} className="p-4 rounded-lg border border-white/10 bg-[#151A23] hover:border-white/20 transition-colors flex items-center justify-between">
                  <div>
                    <div className="font-medium">{c.full_name}</div>
                    <div className="text-xs text-white/50">{c.role}{c.company ? ` · ${c.company}` : ""}</div>
                    {c.email && <div className="text-xs text-white/50">{c.email}</div>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.preventDefault(); setEmailDlg({ open: true, contact: c }); }} className="text-white/70 hover:text-white hover:bg-white/10">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Email
                  </Button>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {tab === "email" && (
          <Section
            title="Send an Email"
            action={<Button onClick={() => openQuick("email")} data-testid="add-email-btn" className="rounded-full bg-white text-black hover:bg-white/90"><Wand2 className="w-4 h-4 mr-2"/>Quick Add</Button>}
          >
            <div className="p-6 rounded-xl border border-white/10 bg-[#151A23]">
              <p className="text-sm text-white/60 mb-4">Draft with Quick Add above, or pick a contact below for the advanced composer. All sends are logged in the contact's history.</p>
              <div className="grid md:grid-cols-2 gap-3">
                {contacts.filter(c => c.email).map(c => (
                  <button
                    key={c.id}
                    onClick={() => setEmailDlg({ open: true, contact: c })}
                    className="text-left p-4 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 transition-colors flex items-center justify-between"
                    data-testid={`email-contact-${c.id}`}
                  >
                    <div>
                      <div className="font-medium">{c.full_name}</div>
                      <div className="text-xs text-white/50">{c.email}</div>
                    </div>
                    <Send className="w-4 h-4 text-white/50" />
                  </button>
                ))}
                {contacts.filter(c => c.email).length === 0 && <Empty label="No contacts with email addresses yet." />}
              </div>
            </div>
          </Section>
        )}
      </div>

      <EntityDialog open={editEntity} setOpen={setEditEntity} entity={entity} onSaved={() => { setEditEntity(false); load(); }} />

      <Dialog open={projRcDlg.open} onOpenChange={(o) => setProjRcDlg({ open: o, project: o ? projRcDlg.project : null })}>
        <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Routing Context · {projRcDlg.project?.name}</DialogTitle>
          </DialogHeader>
          {projRcDlg.project && (
            <RoutingContextEditor
              kind="project" record={projRcDlg.project}
              api={projectsApi} canEdit
              onSaved={async () => {
                const p = await projectsApi.get(projRcDlg.project.id);
                setProjRcDlg({ open: true, project: p.data });
                load();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <ProjectDialog open={projDlg.open} setOpen={(v) => setProjDlg({ ...projDlg, open: v })} project={projDlg.project} entityId={id} contacts={contacts} onSaved={() => { setProjDlg({ open: false, project: null }); load(); }} />
      <TaskDialog open={taskDlg.open} setOpen={(v) => setTaskDlg({ ...taskDlg, open: v })} task={taskDlg.task} entityId={id} projects={projects} onSaved={() => { setTaskDlg({ open: false, task: null }); load(); }} />
      <EventDialog open={evDlg.open} setOpen={(v) => setEvDlg({ ...evDlg, open: v })} event={evDlg.event} entityId={id} projects={projects} contacts={contacts} onSaved={() => { setEvDlg({ open: false, event: null }); load(); }} />
      <ExpenditureDialog open={expDlg.open} setOpen={(v) => setExpDlg({ ...expDlg, open: v })} exp={expDlg.exp} entityId={id} projects={projects} onSaved={() => { setExpDlg({ open: false, exp: null }); load(); }} />
      <EditScopeDialog
        open={scopeDlg.open}
        setOpen={(v) => setScopeDlg({ ...scopeDlg, open: v })}
        onEditOne={() => setExpDlg({ open: true, exp: scopeDlg.exp, initialKind: scopeDlg.exp?.kind || "expense" })}
        onEditFuture={() => scopeDlg.exp?.recurring_rule_id && openRuleEditor(scopeDlg.exp.recurring_rule_id)}
      />
      {/* Template picker (choose which template to launch) */}
      <TemplatePickerDialog
        open={tplPicker}
        setOpen={setTplPicker}
        templates={tplList}
        onPick={(t) => { setTplPicker(false); setTplLaunch({ open: true, template: t }); }}
      />
      <NewFromTemplateDialog
        open={tplLaunch.open}
        setOpen={(v) => setTplLaunch({ ...tplLaunch, open: v })}
        template={tplLaunch.template}
        defaultEntityId={id}
        onCreated={() => { setTplLaunch({ open: false, template: null }); load(); }}
      />
      <NoteDialog open={noteDlg.open} setOpen={(v) => setNoteDlg({ ...noteDlg, open: v })} note={noteDlg.note} entityId={id} projects={projects} contacts={contacts} onSaved={() => { setNoteDlg({ open: false, note: null }); load(); }} />
      <ContactDialog open={contactDlg.open} setOpen={(v) => setContactDlg({ ...contactDlg, open: v })} contact={contactDlg.contact} onSaved={() => { setContactDlg({ open: false, contact: null }); load(); }} />
      <EmailDialog open={emailDlg.open} setOpen={(v) => setEmailDlg({ ...emailDlg, open: v })} contact={emailDlg.contact} entityId={id} onSent={() => { setEmailDlg({ open: false, contact: null }); load(); }} />
      <UniversalQuickAdd
        open={quick.open}
        setOpen={(v) => setQuick({ ...quick, open: v })}
        section={quick.section}
        defaultEntityId={id}
        onCreated={load}
        onEditDetails={handleQuickEditDetails}
      />
    </div>
  );
}

function Panel({ title, value, wide, accent, children }) {
  const accentText = accent === "amber" ? "text-amber-300" : "text-white/90";
  return (
    <div className={`p-5 rounded-xl border ${accent === "amber" ? "border-amber-500/25" : "border-white/10"} bg-[#151A23] ${wide ? "md:col-span-3" : ""}`}>
      <div className="text-eyebrow mb-2">{title}</div>
      <div className={`font-display ${wide ? "text-2xl" : "text-lg"} font-medium ${accentText}`}>{value}</div>
      {children}
    </div>
  );
}
function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-xl font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}
function Empty({ label }) { return <div className="text-sm text-white/40 py-8 text-center border border-dashed border-white/10 rounded-xl">{label}</div>; }

// Lightweight inline dialog for picking a template before launching.
function TemplatePickerDialog({ open, setOpen, templates, onPick }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)} data-testid="template-picker">
      <div className="bg-[#151A23] border border-white/10 rounded-xl p-5 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-eyebrow mb-1">Launch project</div>
        <div className="text-lg font-display font-semibold mb-3">Choose a template</div>
        {(!templates || templates.length === 0) ? (
          <div className="text-sm text-white/50 py-6 text-center">No templates available. Create one from the Templates page.</div>
        ) : (
          <div className="space-y-1">
            {templates.map(t => (
              <button key={t.id} onClick={() => onPick(t)} data-testid={`pick-template-${t.id}`}
                className="w-full text-left p-3 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition">
                <div className="text-white font-medium">{t.name}</div>
                {t.description && <div className="text-xs text-white/50 mt-0.5 line-clamp-2">{t.description}</div>}
                <div className="text-[10px] text-white/40 mt-1.5">{(t.tasks || []).length} tasks · {(t.events || []).length} milestones</div>
              </button>
            ))}
          </div>
        )}
        <div className="mt-4 text-right">
          <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}
function CcyMetric({ label, value, accent }) {
  const accentText = accent === "amber" ? "text-amber-300" : "text-white/90";
  return (
    <div>
      <div className="text-white/40 text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`mt-0.5 ${accentText} tabular-nums`}>{value}</div>
    </div>
  );
}
function RecentActivity({ title, items }) {
  return (
    <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]">
      <div className="text-eyebrow mb-3">{title}</div>
      {items.length === 0 && <div className="text-sm text-white/40">Nothing yet.</div>}
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.id} className="text-sm">
            <div className="text-white/85">{i.primary}</div>
            <div className="text-xs text-white/40">{i.secondary}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
export function TasksList({ tasks, contacts, projects, onEdit, onDelete, onToggle, canDelete }) {
  const navigate = useNavigate();
  const contactById = useMemo(() => Object.fromEntries((contacts || []).map(c => [c.id, c])), [contacts]);
  const projectById = useMemo(() => Object.fromEntries((projects || []).map(p => [p.id, p])), [projects]);
  return (
    <div className="space-y-2">
      {tasks.length === 0 && <Empty label="No tasks." />}
      {tasks.map(t => {
        const overdue = t.due_date && t.status !== "Done" && new Date(t.due_date) < new Date();
        return (
          <div key={t.id} className={`flex items-center justify-between p-4 rounded-lg border ${overdue ? "border-red-500/30" : "border-white/10"} bg-[#151A23] hover:bg-white/[0.04] cursor-pointer transition`}
               data-testid={`task-row-${t.id}`} data-record-id={t.id}
               onClick={(e) => {
                 // Avoid navigating when clicking the checkbox, edit, delete, or inline links.
                 if (e.target.closest("input,button,a")) return;
                 navigate(`/task/${t.id}`);
               }}>
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <input type="checkbox" checked={t.status === "Done"} onChange={() => onToggle(t)} data-testid={`task-check-${t.id}`} className="mt-1 w-4 h-4 accent-emerald-500 cursor-pointer" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge color={STATUS_COLORS[t.status] || "#94A3B8"}>{t.status}</Badge>
                  <Badge color={PRIORITY_COLORS[t.priority] || "#94A3B8"}>{t.priority}</Badge>
                  {t.due_date && <span className={`text-xs ${overdue ? "text-red-400" : "text-white/50"}`}>Due {t.due_date}</span>}
                  {t.project_id && projectById[t.project_id] && <span className="text-xs text-white/40">· {projectById[t.project_id].name}</span>}
                </div>
                <div className={`font-medium ${t.status === "Done" ? "line-through text-white/40" : ""}`}>{t.name}</div>
                {t.assignee_contact_id && contactById[t.assignee_contact_id] && (
                  <div className="text-xs text-white/50 mt-0.5">Assigned to {contactById[t.assignee_contact_id].full_name}</div>
                )}
                {(t.related_contact_id || t.reference_url) && (
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-white/45" data-testid={`task-context-${t.id}`}>
                    {t.related_contact_id && contactById[t.related_contact_id] && (
                      <span data-testid={`task-related-${t.id}`}>
                        <span className="text-white/30">Re:</span>{" "}
                        <span className="text-white/60">{contactById[t.related_contact_id].full_name}</span>
                        {contactById[t.related_contact_id].email && (
                          <span className="text-white/30"> · {contactById[t.related_contact_id].email}</span>
                        )}
                      </span>
                    )}
                    {t.reference_url && (
                      <a
                        href={t.reference_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-300/80 hover:text-blue-200 underline underline-offset-2 truncate max-w-[220px] inline-block align-bottom"
                        data-testid={`task-ref-url-${t.id}`}
                        title={t.reference_url}
                      >
                        {t.reference_url.replace(/^https?:\/\//, "").split("/")[0]}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => onEdit(t)} className="p-1.5 rounded hover:bg-white/10 text-white/60" data-testid={`edit-task-${t.id}`}><Pencil className="w-3.5 h-3.5"/></button>
              {(canDelete ? canDelete(t) : true) && (
                <button onClick={() => onDelete(t)}
                        className="p-1.5 rounded hover:bg-red-500/10 text-red-400"
                        data-testid={`delete-task-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5"/>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
